import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Chip
} from '@mui/material';
import {
  TrendingUp,
  ShoppingCart,
  People,
  Inventory
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import SyncStatus from './SyncStatus';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    pendingOrdersCount: 0,
    recentOrders: [],
    connectionStatus: false
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Test API connection first
      await apiService.healthCheck();
      
      // Load pending orders
      const pendingOrdersResponse = await apiService.getPendingOrders({ per_page: 10 });
      
      setDashboardData({
        pendingOrdersCount: pendingOrdersResponse.data?.length || 0,
        recentOrders: pendingOrdersResponse.data?.slice(0, 5) || [],
        connectionStatus: true
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color = 'primary', onClick }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { transform: 'translateY(-4px)', transition: 'transform 0.2s' } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={color}>
              {value}
            </Typography>
          </Box>
          <Box color={`${color}.main`}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const getOrderStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'processing': 'info',
      'on-hold': 'error',
      'completed': 'success',
      'cancelled': 'default',
      'refunded': 'error',
      'failed': 'error',
      // Custom hiphot.nl statussen
      'intern-magazijn': 'info',
      'logo-ok': 'success',
      'logo-print': 'warning',
      'logo-proofprint': 'warning',
      'logo-received': 'info',
      'design-request': 'warning',
      'logo-request': 'warning'
    };
    return colors[status] || 'default';
  };

  const getOrderStatusLabel = (status) => {
    const labels = {
      'pending': 'In afwachting',
      'processing': 'In behandeling',
      'on-hold': 'Wacht',
      'completed': 'Afgerond',
      'cancelled': 'Geannuleerd',
      'refunded': 'Terugbetaald',
      'failed': 'Mislukt',
      // Custom hiphot.nl statussen
      'intern-magazijn': 'Magazijn HH',
      'logo-ok': 'Logo akkoord',
      'logo-print': 'Logo naar drukker',
      'logo-proofprint': 'Logo proefdruk',
      'logo-received': 'Logo ontvangen',
      'design-request': 'Design opvragen',
      'logo-request': 'Logo opvragen'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Dashboard wordt geladen...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <Button color="inherit" size="small" onClick={loadDashboardData}>
            Opnieuw proberen
          </Button>
        }
      >
        Fout bij het laden van dashboard: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard Overzicht
      </Typography>
      
      {/* Connection Status */}
      <Alert 
        severity={dashboardData.connectionStatus ? 'success' : 'error'} 
        sx={{ mb: 3 }}
      >
        {dashboardData.connectionStatus 
          ? '✅ Verbonden met WooCommerce API' 
          : '❌ Geen verbinding met WooCommerce API'
        }
      </Alert>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Openstaande Orders"
            value={dashboardData.pendingOrdersCount}
            icon={<ShoppingCart fontSize="large" />}
            color="warning"
            onClick={() => navigate('/orders')}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Producten Zoeken"
            value="Zoek"
            icon={<Inventory fontSize="large" />}
            color="primary"
            onClick={() => navigate('/search')}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Verkoopcijfers"
            value="Bekijk"
            icon={<TrendingUp fontSize="large" />}
            color="success"
            onClick={() => navigate('/analytics')}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="API Status"
            value={dashboardData.connectionStatus ? "OK" : "Fout"}
            icon={<People fontSize="large" />}
            color={dashboardData.connectionStatus ? "success" : "error"}
          />
        </Grid>
      </Grid>

      {/* Recent Orders */}
      {dashboardData.recentOrders.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recente Openstaande Orders
            </Typography>
            
            {dashboardData.recentOrders.map((order) => (
              <Box 
                key={order.id} 
                display="flex" 
                justifyContent="space-between" 
                alignItems="center"
                py={1}
                borderBottom="1px solid #f0f0f0"
              >
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Order #{order.number}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {order.billing?.company || `${order.billing?.first_name || ''} ${order.billing?.last_name || ''}`.trim() || 'Onbekend'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {new Date(order.date_created).toLocaleDateString('nl-NL')}
                  </Typography>
                </Box>
                
                <Box textAlign="right">
                  <Typography variant="h6">
                    €{parseFloat(order.total).toFixed(2)}
                  </Typography>
                  <Chip 
                    label={getOrderStatusLabel(order.status)}
                    color={getOrderStatusColor(order.status)}
                    size="small"
                  />
                </Box>
              </Box>
            ))}
            
            <Box mt={2}>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/orders')}
                fullWidth
              >
                Alle openstaande orders bekijken
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Database Sync Status */}
      <Box mt={4}>
        <SyncStatus />
      </Box>

      {/* Quick Actions */}
      {/* Verwijderd: Snelle Acties knoppen */}
    </Box>
  );
};

export default Dashboard;
